import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import createAuth0Client, { Auth0Client } from "@auth0/auth0-spa-js";
import LocalDataService from "@/service/LocalDataService";

export interface Auth0State {
  auth0Client: Auth0Client | null;
  accessToken: string | null;
}

//eslint-disable
@(Module({ dynamic: true, store, name: "auth0", namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class Auth0Module extends VuexModule implements Auth0State {
  public auth0Client: Auth0Client | null = null;
  public accessToken: string | null = null;

  @Action
  public async initClient() {
    if (
      process.env.VUE_APP_AUTH0_DOMAIN &&
      process.env.VUE_APP_AUTH0_CLIENT_ID
    ) {
      if (this.auth0Client) {
        return;
      }
      try {
        const redirectUri = window.location.origin + "/signin";
        const client = await createAuth0Client({
          domain: process.env.VUE_APP_AUTH0_DOMAIN,
          client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
          redirect_uri: redirectUri,
          useRefreshTokens: true,
          cacheLocation: "localstorage",
        });
        this.setClient(client);

        // コールバック時（code あり）は handleRedirectCallback を先に実行する必要があるため、ここでは getIdTokenClaims を呼ばない
        const hasCallbackInUrl =
          typeof window !== "undefined" &&
          window.location.search.includes("code=") &&
          window.location.search.includes("state=");
        if (!hasCallbackInUrl) {
          try {
            const claims = await client.getIdTokenClaims();
            if (claims?.__raw) {
              this.setAccessToken(claims.__raw);
            }
          } catch {
            // Login required などは無視
          }
        }
      } catch (e) {
        if ((e as any)?.error === "interaction_required") {
          // noop
        } else {
          console.warn("initClient:", e);
        }
      }
    }
  }

  @Action
  public async retrieveToken() {
    if (this.auth0Client) {
      const isAuthenticated = await this.auth0Client.isAuthenticated();
      const claims = await this.auth0Client.getIdTokenClaims();
    }
  }

  @Action
  public async refreshToken() {
    if (this.auth0Client) {
      const token = await this.auth0Client.getTokenSilently({
        redirect_uri: window.location.origin + "/signin",
        scope: "openid profile email offline_access",
      });
      if (token) {
        this.setAccessToken(`Bearer ${token}`);
      }
    }
  }

  @Mutation
  public setClient(client: Auth0Client) {
    this.auth0Client = client;
  }

  @Mutation
  public setAccessToken(token: string) {
    this.accessToken = token;
    LocalDataService.setAccessToken(`Bearer ${token}`);
  }

  @Action
  public signIn() {
    if (this.auth0Client) {
      this.auth0Client.loginWithRedirect({
        // redirect_uri: process.env.VUE_APP_AUTH0_REDIRECT_URI,
        redirect_uri: window.location.origin + "/signin",
      });
    }
  }

  @Action
  public logout() {
    if (this.auth0Client) {
      this.auth0Client.logout({
        // returnTo: process.env.VUE_APP_AUTH0_LOGOUT_URI,
        // returnTo: window.location.origin + '/',
        returnTo: window.location.origin + "/signin",
      });
    }
  }

  @Action
  public async handleAuth0() {
    if (!this.auth0Client || !process.env.VUE_APP_AUTH0_DOMAIN) return;
    try {
      await this.auth0Client.handleRedirectCallback();
      const claim = await this.auth0Client.getIdTokenClaims();
      const accessToken = claim?.__raw;
      if (accessToken) {
        this.setAccessToken(accessToken);
      }
    } catch (e) {
      console.warn("handleAuth0:", e);
      throw e;
    }
  }
}

export const auth0Module = getModule(Auth0Module);

/*

import LocalDataService from '@/service/LocalDataService';
import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store/index';
import createAuth0Client, { Auth0Client } from '@auth0/auth0-spa-js';

export interface Auth0State {
  auth0Client: Auth0Client | null;
}

@(Module({ dynamic: true, store, name: 'auth0', namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class Auth0Module extends VuexModule implements Auth0State {
  public auth0Client: Auth0Client | null = null;

  @Action({ rawError: true })
  public async initClient() {
    if (
      process.env.VUE_APP_AUTH0_DOMAIN &&
      process.env.VUE_APP_AUTH0_CLIENT_ID
    ) {
      try {
        const client = await createAuth0Client({
          domain: process.env.VUE_APP_AUTH0_DOMAIN,
          client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
          redirect_uri: window.location.origin + '/signin',
          useRefreshTokens: true,
        });
        console.log(`client: ${JSON.stringify(client)}`);
        this.setClient(client);
      } catch (e) {
        const err = e as { error?: string } | null;
        if (err && err.error === 'interaction_required') {
        }
      }
    }
  }

  @Mutation
  public setClient(client: Auth0Client) {
    this.auth0Client = client;
  }

  @Action({ rawError: true })
  public async signinProcess() {
    console.log('auth', this.auth0Client)
    if (this.auth0Client) {
      await this.auth0Client.loginWithRedirect({
        redirect_uri: window.location.origin + '/signin',
      });
    }
  }

  @Action({ rawError: true })
  public async getAuth0User() {
    if (this.auth0Client && process.env.VUE_APP_AUTH0_DOMAIN) {
      const user = await this.auth0Client.getUser();
      return user;
    }
    return null;
  }

  @Action({ rawError: true })
  public async handleAuth0() {
    if (this.auth0Client && process.env.VUE_APP_AUTH0_DOMAIN) {
      await this.auth0Client.handleRedirectCallback();
      const user = await this.auth0Client.getUser();
      // const claim = await this.auth0Client.getIdTokenClaims();
      // const accessToken = claim.__raw;
      // LocalDataService.setAccessToken(accessToken);
      return user;
    }
    return null;
  }

  @Action({ rawError: true })
  public async isAuthenticated() {
    if (this.auth0Client) {
      const response = await this.auth0Client.isAuthenticated();
      return response;
    }
  }

  @Action({ rawError: true })
  public async getTokenSilently() {
    try {
      if (this.auth0Client) {
        const response = await this.auth0Client.getTokenSilently();
        return response;
      }
    } catch (error) {
      throw error;
    }
  }

  @Action({ rawError: true })
  public async logout() {
    if (!this.auth0Client) {
      await this.initClient();
    }
    if (this.auth0Client) {
      //await LocalDataService.removeItem();
      await this.auth0Client.logout();
    }
  }

  @Action({ rawError: true })
  public async getAuth0Token() {
    if (this.auth0Client) {
      const claims = await this.auth0Client.getIdTokenClaims();
      if (claims) {
        return claims.__raw;
      }
      await this.initClient();
      const claims2 = await this.auth0Client.getIdTokenClaims();
      if (claims2) {
        return claims2.__raw;
      }
      return '';
    } else {
      try {
        await this.initClient();
        const claims = await this.auth0Client!.getIdTokenClaims();
        if (claims) {
          return claims.__raw;
        } else {
          // this.logout();
        }
        return '';
      } catch (err) {
        return '';
      }
    }
  }
}

export const auth0Module = getModule(Auth0Module);
*/
